import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  CreateDashboardCardInput,
  CreateDashboardInput,
  Dashboard,
  DashboardDetail,
  DashboardDrillDown,
  DashboardEntityCatalog,
  DashboardRunResult,
  MemberPreferences,
  UpdateDashboardCardInput,
  UpdateDashboardInput,
  UpdateDashboardLayoutInput,
} from '@catalyst-psa/shared';

import { api } from '../../api/client.js';

export const dashboardKeys = {
  all: ['dashboards'] as const,
  list: () => ['dashboards', 'list'] as const,
  detail: (id: string) => ['dashboards', 'detail', id] as const,
  run: (id: string) => ['dashboards', 'run', id] as const,
  catalog: () => ['dashboards', 'catalog'] as const,
};

/** Key for the member's saved preferences, which carry the pinned landing dashboard. */
export const preferencesKey = ['preferences'] as const;

/**
 * The member's saved preferences. Used here only for `defaultDashboardId` (the pinned
 * landing page); the appearance fields are owned by ThemeProvider.
 */
export function useMemberPreferences(): UseQueryResult<MemberPreferences> {
  return useQuery({
    queryKey: preferencesKey,
    queryFn: () => api.get<MemberPreferences>('/preferences'),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Pins (or clears) the dashboard a member lands on when they open the app. The PUT sends
 * only this key, so the member's theme and accent are left untouched server-side.
 */
export function useSetPinnedDashboard(): UseMutationResult<
  MemberPreferences,
  unknown,
  string | null
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dashboardId: string | null) =>
      api.put<MemberPreferences>('/preferences', { defaultDashboardId: dashboardId }),
    onSuccess: (data) => {
      queryClient.setQueryData(preferencesKey, data);
    },
  });
}

/** The field catalog that drives the card builder. Static for a session. */
export function useDashboardCatalog(
  enabled = true,
): UseQueryResult<DashboardEntityCatalog[]> {
  return useQuery({
    queryKey: dashboardKeys.catalog(),
    queryFn: () => api.get<DashboardEntityCatalog[]>('/dashboards/catalog'),
    staleTime: Number.POSITIVE_INFINITY,
    enabled,
  });
}

export function useDashboards(): UseQueryResult<Dashboard[]> {
  return useQuery({
    queryKey: dashboardKeys.list(),
    queryFn: () => api.get<Dashboard[]>('/dashboards'),
  });
}

export function useDashboard(id: string | undefined): UseQueryResult<DashboardDetail> {
  return useQuery({
    queryKey: dashboardKeys.detail(id ?? ''),
    queryFn: () => api.get<DashboardDetail>(`/dashboards/${id ?? ''}`),
    enabled: Boolean(id),
  });
}

/**
 * Runs every card. Kept as a separate query from the definition so a refresh re-reads the
 * data without re-fetching (or re-rendering) the layout.
 */
export function useDashboardRun(id: string | undefined): UseQueryResult<DashboardRunResult> {
  return useQuery({
    queryKey: dashboardKeys.run(id ?? ''),
    queryFn: () => api.post<DashboardRunResult>(`/dashboards/${id ?? ''}/run`),
    enabled: Boolean(id),
  });
}

export function useCreateDashboard(): UseMutationResult<
  DashboardDetail,
  unknown,
  CreateDashboardInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDashboardInput) =>
      api.post<DashboardDetail>('/dashboards', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
    },
  });
}

export interface UpdateDashboardArgs {
  id: string;
  input: UpdateDashboardInput;
}

export function useUpdateDashboard(): UseMutationResult<
  DashboardDetail,
  unknown,
  UpdateDashboardArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: UpdateDashboardArgs) =>
      api.patch<DashboardDetail>(`/dashboards/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
    },
  });
}

export function useDeleteDashboard(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/dashboards/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
      // Deleting a dashboard clears it from anyone who pinned it as their landing page.
      void queryClient.invalidateQueries({ queryKey: preferencesKey });
    },
  });
}

export interface AddCardArgs {
  dashboardId: string;
  input: CreateDashboardCardInput;
}

export function useAddDashboardCard(): UseMutationResult<DashboardDetail, unknown, AddCardArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dashboardId, input }: AddCardArgs) =>
      api.post<DashboardDetail>(`/dashboards/${dashboardId}/cards`, input),
    onSuccess: (_data, { dashboardId }) => {
      void queryClient.invalidateQueries({ queryKey: dashboardKeys.detail(dashboardId) });
      void queryClient.invalidateQueries({ queryKey: dashboardKeys.run(dashboardId) });
      void queryClient.invalidateQueries({ queryKey: dashboardKeys.list() });
    },
  });
}

export interface UpdateCardArgs {
  dashboardId: string;
  cardId: string;
  input: UpdateDashboardCardInput;
}

export function useUpdateDashboardCard(): UseMutationResult<
  DashboardDetail,
  unknown,
  UpdateCardArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dashboardId, cardId, input }: UpdateCardArgs) =>
      api.patch<DashboardDetail>(`/dashboards/${dashboardId}/cards/${cardId}`, input),
    onSuccess: (_data, { dashboardId }) => {
      void queryClient.invalidateQueries({ queryKey: dashboardKeys.detail(dashboardId) });
      void queryClient.invalidateQueries({ queryKey: dashboardKeys.run(dashboardId) });
    },
  });
}

export interface RemoveCardArgs {
  dashboardId: string;
  cardId: string;
}

/** Copy a card, config and all, onto the bottom of the same dashboard. */
export function useDuplicateDashboardCard(): UseMutationResult<
  DashboardDetail,
  unknown,
  RemoveCardArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dashboardId, cardId }: RemoveCardArgs) =>
      api.post<DashboardDetail>(`/dashboards/${dashboardId}/cards/${cardId}/duplicate`),
    onSuccess: (_data, { dashboardId }) => {
      void queryClient.invalidateQueries({ queryKey: dashboardKeys.detail(dashboardId) });
      void queryClient.invalidateQueries({ queryKey: dashboardKeys.run(dashboardId) });
      void queryClient.invalidateQueries({ queryKey: dashboardKeys.list() });
    },
  });
}

export function useRemoveDashboardCard(): UseMutationResult<
  DashboardDetail,
  unknown,
  RemoveCardArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dashboardId, cardId }: RemoveCardArgs) =>
      api.delete<DashboardDetail>(`/dashboards/${dashboardId}/cards/${cardId}`),
    onSuccess: (_data, { dashboardId }) => {
      void queryClient.invalidateQueries({ queryKey: dashboardKeys.detail(dashboardId) });
      void queryClient.invalidateQueries({ queryKey: dashboardKeys.run(dashboardId) });
      void queryClient.invalidateQueries({ queryKey: dashboardKeys.list() });
    },
  });
}

export interface SaveLayoutArgs {
  dashboardId: string;
  input: UpdateDashboardLayoutInput;
}

export function useSaveDashboardLayout(): UseMutationResult<
  DashboardDetail,
  unknown,
  SaveLayoutArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dashboardId, input }: SaveLayoutArgs) =>
      api.put<DashboardDetail>(`/dashboards/${dashboardId}/layout`, input),
    onSuccess: (_data, { dashboardId }) => {
      void queryClient.invalidateQueries({ queryKey: dashboardKeys.detail(dashboardId) });
    },
  });
}

export interface DrillDownArgs {
  dashboardId: string;
  cardId: string;
  groupKey?: string | null;
}

export function useDashboardDrillDown(): UseMutationResult<
  DashboardDrillDown,
  unknown,
  DrillDownArgs
> {
  return useMutation({
    mutationFn: ({ dashboardId, cardId, groupKey }: DrillDownArgs) =>
      api.post<DashboardDrillDown>(`/dashboards/${dashboardId}/cards/${cardId}/drill-down`, {
        groupKey: groupKey ?? null,
      }),
  });
}
